import axios from "axios";
import React, { useState } from "react";
import { api_url } from "../../../constant";
import { toast } from "react-toastify";
import ServiceSuggestion from "../../../components/services/service-suggestion";


const SingleInvoice = ({ invoice }) => {
    const [invoiceStatus, setInvoiceStatus] = useState(invoice.status);
    const [showConfirm, setShowConfirm] = useState(null); // 'accept' | 'reject' | null
    const currentUserId = localStorage.getItem("user_id");
    const isCurrentUserSeller = currentUserId === invoice.negotiation?.sellerId;
    const [rejectionReason, setRejectionReason] = useState("");

    const token = localStorage.getItem("token");

    // Format date function
    const formatDate = (dateString) => {
        if (!dateString) return "غير محدد";

        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString; // Return original if invalid date

            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch (error) {
            console.error("Error formatting date:", error);
            return dateString;
        }
    };

    const handleAcceptInvoice = async () => {
        try {
            await axios.patch(`${api_url}/invoices/accept/${invoice?.invoiceId}`, {}, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setInvoiceStatus("ACCEPTED");
            toast.success("تم قبول الفاتورة بنجاح!");
        } catch (error) {
            console.error("Error accepting invoice:", error.response?.data || error.message);
            toast.error("فشل في قبول الفاتورة");
        } finally {
            setShowConfirm(null);
        }
    };

    const handleRejectInvoice = async () => {
        if (!rejectionReason.trim()) {
            toast.error("يرجى كتابة سبب الرفض قبل تأكيد العملية.");
            return;
        }
        try {
            await axios.patch(`${api_url}/invoices/reject/${invoice?.invoiceId}`, { rejectionReason }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setInvoiceStatus("REJECTED");
            setRejectionReason("");
            toast.success("تم رفض الفاتورة بنجاح!");
        } catch (error) {
            console.error("Error rejecting invoice:", error.response?.data || error.message);
            toast.error("فشل في رفض الفاتورة");
        } finally {
            setShowConfirm(null);
        }
    };

    const renderConfirmModal = () => {
        if (!showConfirm) return null;

        const isAccept = showConfirm === "accept";
        const message = isAccept
            ? "هل أنت متأكد من قبول الفاتورة؟"
            : "هل أنت متأكد من رفض الفاتورة؟";
        const confirmHandler = isAccept ? handleAcceptInvoice : handleRejectInvoice;



        return (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                <div className="bg-white p-6 rounded shadow-xl max-w-sm w-full text-center">
                    <p className="mb-4 text-lg font-medium">{message}</p>

                    {!isAccept && (
                        <textarea
                            placeholder="يرجى كتابة سبب الرفض"
                            className="w-full p-2 border border-gray-300 rounded mb-4 text-sm"
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            rows={3}
                        />
                    )}

                    <div className="flex justify-center gap-4 mt-4">
                        <button
                            onClick={confirmHandler}
                            className={`px-4 py-2 rounded font-bold text-white ${isAccept ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'}`}
                        >
                            تأكيد
                        </button>
                        <button
                            onClick={() => {
                                setShowConfirm(null);
                                setRejectionReason("");
                            }}
                            className="px-4 py-2 rounded border border-gray-400 text-gray-600 hover:bg-gray-100"
                        >
                            إلغاء
                        </button>
                    </div>
                </div>
            </div>
        );
    };


    return (
        <>
            <div dir="rtl" className="max-w-4xl w-full mx-auto bg-white shadow-xl rounded-xl overflow-hidden border border-gray-200">
                {renderConfirmModal()}

                {/* Invoice Header */}
                <div className="bg-blue-600 p-6 text-white">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
                        <div>
                            <h1 className="text-2xl md:text-3xl font-bold">فاتورة بيع</h1>
                            <p className="text-primary-100 mt-1">رقم الفاتورة: #{invoice?.invoiceId}</p>
                        </div>
                        <div className="mt-4 md:mt-0">
                            <p className="text-primary-100">تاريخ الإصدار</p>
                            <p className="font-medium text-right">{formatDate(invoice?.issuedDate)}</p>
                        </div>
                    </div>
                </div>

                {/* Parties Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 border-b">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-700 mb-2">البائع</h3>
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <p className="font-medium text-gray-800 text-right">{invoice?.seller}</p>
                        </div>
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-gray-700 mb-2">المشتري</h3>
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <p className="font-medium text-gray-800 text-right">{invoice?.buyer}</p>
                        </div>
                    </div>
                </div>

                {/* Items Table */}
                <div className="p-6">
                    <h3 className="text-lg font-semibold text-gray-700 mb-4 text-right">تفاصيل الفاتورة</h3>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="bg-gray-100 text-gray-700">
                                    <th className="text-right py-3 px-4 font-semibold rounded-tr-lg">المحصول</th>
                                    <th className="text-center py-3 px-4 font-semibold">الكمية</th>
                                    <th className="text-center py-3 px-4 font-semibold">الوحدة</th>
                                    <th className="text-center py-3 px-4 font-semibold">السعر</th>
                                    <th className="text-center py-3 px-4 font-semibold rounded-tl-lg">المجموع</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr className="border-b border-gray-200">
                                    <td className="py-4 px-4 font-medium text-gray-800 text-right">{invoice?.cropName}</td>
                                    <td className="py-4 px-4 text-center">{invoice?.quantity}</td>
                                    <td className="py-4 px-4 text-center">{invoice?.unit.name}</td>
                                    <td className="py-4 px-4 text-center">{invoice?.amount} جنيه</td>
                                    <td className="py-4 px-4 text-center font-medium">{invoice?.amount} جنيه</td>
                                </tr>
                                {/* Total Row */}
                                <tr className="bg-gray-50">
                                    <td colSpan="4" className="py-3 px-4 font-semibold text-right">المجموع الكلي:</td>
                                    <td className="py-3 px-4 font-bold text-primary-600 text-center">{invoice?.amount} جنيه سوداني</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Delivery and Payment Details */}
                <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6 border-t">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-700 mb-3 text-right">تفاصيل التسليم</h3>
                        <div className="space-y-3 text-right">
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                <p className="text-sm text-gray-500">مكان التسليم</p>
                                <p className="font-medium">{invoice?.deliveryPlace}</p>
                            </div>
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                <p className="text-sm text-gray-500">مسؤولية التسليم</p>
                                <p className="font-medium">
                                    {invoice?.deliveryResponsibility === "seller" ? "البائع" : "المشتري"}
                                </p>
                            </div>
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                <p className="text-sm text-gray-500">تاريخ التسليم</p>
                                <p className="font-medium">{formatDate(invoice?.deliveryDate)}</p>
                            </div>
                        </div>
                    </div>

                    <div>
                        <h3 className="text-lg font-semibold text-gray-700 mb-3 text-right">شروط الدفع</h3>
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 min-h-[260px]">
                            <p className="whitespace-pre-line text-right">
                                {invoice?.paymentTerms || "لا توجد شروط دفع محددة"}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Notes */}
                {invoice?.notes && (
                    <div className="px-6 pb-6 border-t">
                        <h3 className="text-lg font-semibold text-gray-700 mb-2 text-right">ملاحظات</h3>
                        <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                            <p className="whitespace-pre-line text-blue-800 text-right">{invoice?.notes}</p>
                        </div>
                    </div>
                )}

                {/* Action Buttons */}
                {invoiceStatus === "pending" && !isCurrentUserSeller && (
                    <div className="p-6 border-t bg-gray-50">
                        <div className="flex flex-col sm:flex-row justify-center gap-4">
                            <button
                                className="bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-lg transition flex items-center justify-center gap-2"
                                onClick={() => setShowConfirm("accept")}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                قبول الفاتورة
                            </button>
                            <button
                                className="bg-red-500 hover:bg-red-600 text-white font-bold py-3 px-6 rounded-lg transition flex items-center justify-center gap-2"
                                onClick={() => setShowConfirm("reject")}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                                رفض الفاتورة
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <div dir="rtl" className="max-w-4xl mx-auto mt-20">
                <ServiceSuggestion />
            </div>
        </>
    );
};

export default SingleInvoice;
